import React from "react"
import Icon from "@kit/design/icon"
import Stack from "@kit/design/stack"
import { MenuLink, MenuItem } from "@reach/menu-button"
import VisuallyHidden from "@kit/design/visually-hidden"
import { Avatar } from "core/avatar"
import analytics from "utils/analytics"
import { slugify } from "core/utils/slugify"

function trackSettingsClick(label) {
  analytics.track(
    "Element",
    "Clicked",
    {
      element_type: "link",
      name: `nav_${slugify(label)}`,
      text: label,
      location: "settings_nav"
    },
    { transport: "beacon" }
  )
}

const SettingsLink = ({ href, method, newTab, icon, color, label }) => (
  <MenuLink
    className="settings-link rounded"
    href={href}
    data-method={method != "get" ? method : undefined} // data-method="get" blocks cmd-click
    rel="nofollow"
    target={newTab ? "_blank" : undefined}
    onClick={() => trackSettingsClick(label)}
  >
    <Icon
      name={icon}
      className={`align-middle mr-4 w-6 h-6 ${color}`}
      aria-hidden
    />
    <span className="capitalize font-semibold text-gray-900">{label}</span>
  </MenuLink>
)

export const SettingsItem = ({
  label,
  icon,
  data,
  color = "",
  href = "/",
  method = "get",
  newTab = false
}) => {
  const menuLabel = label.toLowerCase()

  const handleHelpClick = link => {
    /* eslint-disable-next-line no-undef */
    if (typeof Intercom === "undefined" || !Intercom) {
      window.open(link, "_blank")
      return
    }

    /* eslint-disable-next-line no-undef */
    const intercom = Intercom
    // Just show intercom. The user can see previous conversations or start a new one.
    intercom("show")

    return
  }

  // Our help link in our settings menu needs to handle whether or not Intercom is available
  if (menuLabel === "help") {
    return (
      <MenuItem
        className="settings-link rounded"
        onSelect={() => {
          trackSettingsClick(label)
          handleHelpClick(href)
        }}
      >
        <Icon
          name={icon}
          className={`align-middle mr-4 w-6 h-6 ${color}`}
          aria-hidden
        />
        <span className="capitalize font-semibold text-gray-800">{label}</span>
      </MenuItem>
    )
  }

  // If there are users we need to build out each link to switch between user accounts
  if (data) {
    return (
      <>
        <hr className="my-2" />
        <p className="font-semibold text-gray-900 px-2 text-sm leading-10 mb-2">
          {label}
        </p>
        <Stack spacing={2}>
          {data.map(account => (
            <MenuLink
              className="settings-link rounded inline-flex gap-4"
              key={account.id}
              href={account.href}
              data-method={method}
              data-account-id={account.account_id}
              data-account-email={account.email}
              rel="nofollow"
            >
              <Avatar email={account.email} shape="square" />
              <VisuallyHidden>{account.label}</VisuallyHidden>
              <span
                className={`align-middle ${color} w-full truncate whitespace-nowrap`}
                title={account.label}
                aria-hidden="true"
              >
                {account.label}
              </span>
            </MenuLink>
          ))}
        </Stack>
      </>
    )
  }

  // by default we're just building our standard settings menu link
  return (
    <>
      <SettingsLink
        href={href}
        method={method}
        newTab={newTab}
        icon={icon}
        color={color}
        label={label}
      />
      {menuLabel === "view account in admin" && <hr className="my-2" />}
    </>
  )
}
